import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { setLanguageAction } from 'root-redux/actions/common'
import {
  selectCurrentVariant,
  selectCurrentVariantParentCohort,
  selectIsInContextShown,
} from 'root-redux/selects/common'

import { getCurrentEnv } from 'helpers/getCurrentEnv'

import { browserHistory } from 'browser-history'
import {
  COHORT_QUERY_PARAM,
  Cohort,
  CurrentEnvironment,
  INITIAL_LANGUAGE,
  LANG_QUERY_PARAM,
  Locale,
  SUPPORTED_LOCALES_MAP,
} from 'root-constants'

export const useLanguage = (): void => {
  const { pathname, search } = document.location
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const parentCohort = useSelector(selectCurrentVariantParentCohort)
  const variant = useSelector(selectCurrentVariant, shallowEqual)
  const isInContextShown = useSelector(selectIsInContextShown)

  const isTestEnvironment = getCurrentEnv() !== CurrentEnvironment.PROD

  const isInContextActive = useMemo(
    () => isTestEnvironment && isInContextShown,
    [isTestEnvironment, isInContextShown],
  )

  useEffect(() => {
    if (!variant) return
    const URLParams = new URLSearchParams(search)
    const langQueryParam = URLParams.get(LANG_QUERY_PARAM)
    const langParam = langQueryParam || i18n.resolvedLanguage || ''
    const cohort = URLParams.get(COHORT_QUERY_PARAM)

    const supportedLocales =
      SUPPORTED_LOCALES_MAP[(parentCohort as Cohort) || (cohort as Cohort)]
    if (!supportedLocales?.includes(langParam)) {
      dispatch(
        setLanguageAction(
          isInContextActive ? Locale.IN_CONTEXT : INITIAL_LANGUAGE,
        ),
      )

      URLParams.set(LANG_QUERY_PARAM, INITIAL_LANGUAGE)
      browserHistory.replace({
        pathname,
        search: URLParams.toString(),
      })
      return
    }

    dispatch(
      setLanguageAction(isInContextActive ? Locale.IN_CONTEXT : langParam),
    )

    if (!langQueryParam) {
      URLParams.set(LANG_QUERY_PARAM, langParam)
      browserHistory.replace({
        pathname,
        search: URLParams.toString(),
      })
    }
  }, [
    dispatch,
    pathname,
    search,
    parentCohort,
    variant,
    isInContextActive,
    i18n.resolvedLanguage,
  ])
}
